// Phase 20 · Community V3 · Event detail (public overview + RSVP + Q&A + chat sidebar)
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Users, Radio, PlayCircle, StopCircle, Flag, Share2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "sonner";
import EventStreamPlayer from "../../../components/community_v3/EventStreamPlayer";
import EventQAPanel from "../../../components/community_v3/EventQAPanel";
import EventRsvpButton from "../../../components/community_v3/EventRsvpButton";
import EventLiveChat from "../../../components/community_v3/EventLiveChat";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
    try { return new Date(iso).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function EventDetail() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [ev, setEv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [reportReason, setReportReason] = useState("spam");

    const load = async () => {
        setLoading(true); setDisabled(false);
        try {
            const r = await fetch(`${API}/api/community/events/${eventId}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); return; }
            if (r.status === 404) { setEv(null); return; }
            if (r.ok) setEv(await r.json());
        } catch {}
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

    const goLive = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/events/${ev.id}/go-live`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("You're live!"); load(); }
            else toast.error("Go live failed");
        } catch {}
        setBusy(false);
    };

    const endEvent = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/events/${ev.id}/end`, { method: "POST", credentials: "include" });
            if (r.ok) { toast.success("Event ended"); load(); }
            else toast.error("End failed");
        } catch {}
        setBusy(false);
    };

    const submitReport = async () => {
        try {
            const r = await fetch(`${API}/api/community/events/${ev.id}/report`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ reason: reportReason }),
            });
            if (r.ok) { toast.success("Reported"); setReportOpen(false); }
            else toast.error("Report failed");
        } catch {}
    };

    const shareLink = () => {
        const url = `${window.location.origin}/events/${ev?.slug || ev?.id}`;
        navigator.clipboard?.writeText(url);
        toast.success("Link copied");
    };

    if (loading) return <div className="min-h-screen bg-[#0A0A0A] text-neutral-500 flex items-center justify-center">Loading…</div>;
    if (disabled) return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA] flex items-center justify-center p-6">
            <div className="text-center space-y-2">
                <p className="text-lg font-semibold">Events engine disabled</p>
                <Link to="/community" className="text-neutral-400 hover:text-neutral-200 underline">← Community</Link>
            </div>
        </div>
    );
    if (!ev) return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-500 flex items-center justify-center p-6">
            <div className="text-center space-y-2">
                <p>Event not found</p>
                <Link to="/events" className="text-[#FFFFFF] hover:underline">← Back to Events</Link>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="event-detail-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/events" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Events
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold truncate" data-testid="event-title-header">{ev.title}</h1>
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            type="button"
                            onClick={shareLink}
                            className="w-8 h-8 rounded-lg border border-[#1A1A1A] text-neutral-400 hover:text-[#FFFFFF] hover:border-[#FFFFFF]/40 flex items-center justify-center"
                            title="Share"
                            data-testid="event-share-btn"
                        >
                            <Share2 size={13} />
                        </button>
                        {user && !ev.is_host && (
                            <button
                                type="button"
                                onClick={() => setReportOpen(true)}
                                className="w-8 h-8 rounded-lg border border-[#1A1A1A] text-neutral-400 hover:text-[#FAFAFA] hover:border-[#FAFAFA]/40 flex items-center justify-center"
                                title="Report"
                                data-testid="event-report-btn"
                            >
                                <Flag size={13} />
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                <section className="space-y-4">
                    <EventStreamPlayer event={ev} />
                    <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFFFFF]/10 border border-[#FFFFFF]/40 text-[#FFFFFF] font-bold">
                                        {ev.event_type.replace("_", " ")}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">{ev.category_slug}</span>
                                    {ev.status === "live" && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FAFAFA] text-black font-bold animate-pulse">
                                            <Radio size={9} /> LIVE
                                        </span>
                                    )}
                                    {ev.status === "ended" && (
                                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-neutral-700/30 border border-neutral-700/50 text-neutral-400 font-bold">
                                            Ended
                                        </span>
                                    )}
                                    {ev.status === "cancelled" && (
                                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FAFAFA]/10 border border-[#FAFAFA]/40 text-[#FAFAFA] font-bold">
                                            Cancelled
                                        </span>
                                    )}
                                </div>
                                <h2 className="text-2xl font-bold text-neutral-100 mt-1" data-testid="event-title">{ev.title}</h2>
                                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-neutral-400">
                                    <span className="inline-flex items-center gap-1"><Calendar size={12} /> {fmtDate(ev.scheduled_start)}</span>
                                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {ev.duration_minutes} min</span>
                                    <span className="inline-flex items-center gap-1"><Users size={12} /> {ev.attendee_count} registered</span>
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-neutral-900 text-[10px] font-bold">
                                        {ev.host_username?.[0]?.toUpperCase() || "?"}
                                    </div>
                                    <Link to={`/profile/${ev.host_username}`} className="text-xs text-neutral-300 hover:text-[#FFFFFF]">
                                        @{ev.host_username}
                                    </Link>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {ev.is_host && ev.status === "scheduled" && (
                                    <button
                                        type="button"
                                        onClick={goLive}
                                        disabled={busy}
                                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-white font-semibold rounded-xl text-xs"
                                        data-testid="event-go-live-btn"
                                    >
                                        <PlayCircle size={14} /> Go live
                                    </button>
                                )}
                                {ev.is_host && ev.status === "live" && (
                                    <button
                                        type="button"
                                        onClick={endEvent}
                                        disabled={busy}
                                        className="inline-flex items-center gap-2 px-3 py-2 bg-neutral-700/40 hover:bg-neutral-700/60 text-neutral-100 font-semibold rounded-xl text-xs"
                                        data-testid="event-end-btn"
                                    >
                                        <StopCircle size={14} /> End
                                    </button>
                                )}
                                {!ev.is_host && (
                                    <EventRsvpButton event={ev} viewer={user} onChange={load} />
                                )}
                            </div>
                        </div>
                        {ev.description && (
                            <p className="text-sm text-neutral-300 whitespace-pre-wrap" data-testid="event-description">
                                {ev.description}
                            </p>
                        )}
                        {ev.rules && (
                            <div className="mt-2 bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-3">
                                <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">Ground rules</p>
                                <p className="text-xs text-neutral-300 whitespace-pre-wrap">{ev.rules}</p>
                            </div>
                        )}
                    </div>

                    <EventQAPanel event={ev} viewer={user} />
                </section>

                <aside className="lg:sticky lg:top-[68px] lg:self-start h-[600px]" data-testid="event-side-chat">
                    {ev.status !== "cancelled" && (
                        <EventLiveChat event={ev} viewer={user} canModerate={ev.is_host} />
                    )}
                </aside>
            </main>

            {reportOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
                    <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl w-full max-w-md p-5">
                        <h3 className="text-sm font-semibold text-neutral-100 flex items-center gap-2 mb-3">
                            <Flag size={16} className="text-[#FAFAFA]" /> Report event
                        </h3>
                        <select
                            value={reportReason}
                            onChange={(e) => setReportReason(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none mb-4"
                            data-testid="event-report-reason"
                        >
                            <option value="spam">Spam</option>
                            <option value="harassment">Harassment</option>
                            <option value="offensive">Offensive content</option>
                            <option value="misleading">Misleading</option>
                            <option value="other">Other</option>
                        </select>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setReportOpen(false)} className="px-3 py-1.5 text-neutral-400 text-sm">Cancel</button>
                            <button
                                type="button"
                                onClick={submitReport}
                                className="px-3 py-1.5 bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-white font-semibold rounded-lg text-sm"
                                data-testid="event-report-submit"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
